/**
 * app.js — Entry point. DOMContentLoaded bootstrap.
 * Xeric Theme
 *
 * Architecture:
 *   initOnce()           — run ONCE on first load for persistent shell features
 *   initAll(container)   — run after every Barba enter; binds to container or globals idempotently
 *   destroyAll()         — teardown listeners created by initAll
 *
 * Depends on:
 *   window.XericAnimations  (animations.js)
 *   window.XericCart        (cart.js)
 *   window.XericBarba       (barba.js)
 */

(function (global) {
  'use strict';

  // ── Cleanup registry ─────────────────────────────────────────
  // Functions registered here are called by destroyAll() before each leave.
  let _destroyFns = [];

  function registerDestroy(fn) {
    _destroyFns.push(fn);
  }

  // ── Header height sync ──────────────────────────────────────────────────────
  // --header-h is the live measured height of #site-header so the panel top,
  // panel-h, and panel-closed-y all recalculate automatically when the header
  // grows (e.g. switches from horizontal to vertical layout at 1240px).

  function updateHeaderH() {
    var header = document.getElementById('site-header');
    if (!header) return;
    var h = Math.ceil(header.offsetHeight);
    document.documentElement.style.setProperty('--header-h', h + 'px');
  }

  // ── Mobile tab anchor ───────────────────────────────────────────────────────
  // --mob-tab-top is the live bottom edge of the header logo + links stack.
  // Mobile folder-tab CSS rules use it as their "top" anchor so the tabs park
  // exactly where the header content ends, regardless of image load or font size.
  // Falls back to var(--header-h) in CSS if the variable hasn't been set yet.

  function updateMobTabTop() {
    if (!window.matchMedia('(max-width: 768px)').matches) return;
    var header = document.getElementById('site-header');
    if (!header) return;
    // Anchor from the header's true bottom edge (not the links row) so that
    // --mob-tab-top = header-h + header-gap, matching the panel's top formula
    // (header-h + header-gap + tabs-h) and eliminating the gap between the
    // last mobile tab and the panel top.
    var bottom = header.getBoundingClientRect().bottom;
    var gap = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-gap')) || 0;
    document.documentElement.style.setProperty('--mob-tab-top', Math.ceil(bottom + gap) + 'px');
  }

  // ── Hero Drag — draggable blocks on the home-hero background ──

  function initHeroDrag() {
    var hero     = document.querySelector('.home-hero');
    if (!hero) return;
    var dragArea = hero.querySelector('.hero-drag-area');
    if (!dragArea) return;
    var blocks = Array.prototype.slice.call(dragArea.querySelectorAll('.hero-block'));
    if (!blocks.length) return;
    var _heroBlocksRandomized = false;

    // ── Randomise initial positions ───────────────────────────
    // Wait for window.load so image heights are resolved before placing blocks.
    // Distributes every block to a fully random position inside the drag area.
    function randomizeHeroBlocks() {
      var areaRect = dragArea.getBoundingClientRect();
      var areaW    = areaRect.width;
      var areaH    = areaRect.height;
      blocks.forEach(function (block) {
        var blockRect = block.getBoundingClientRect();
        var bw = blockRect.width || block.offsetWidth || parseFloat(getComputedStyle(block).width) || 280;
        var bh = blockRect.height || block.offsetHeight || parseFloat(getComputedStyle(block).height) || Math.round(window.innerHeight * 0.25);
        var maxX = Math.max(0, areaW - bw);
        var maxY = Math.max(0, areaH - bh);
        block.style.left      = Math.floor(Math.random() * maxX) + 'px';
        block.style.top       = Math.floor(Math.random() * maxY) + 'px';
        block.style.transform = 'translateZ(0)';
      });
      _heroBlocksRandomized = true;
    }

    function isDragAreaReady() {
      var areaRect = dragArea.getBoundingClientRect();
      return areaRect.width > 0 && areaRect.height > 0;
    }

    function randomizeHeroBlocksWhenReady(attemptsLeft) {
      if (_heroBlocksRandomized) return;
      if (isDragAreaReady()) {
        randomizeHeroBlocks();
        return;
      }

      if (attemptsLeft <= 0) {
        // Last-resort fallback to avoid leaving blocks unpositioned.
        randomizeHeroBlocks();
        return;
      }

      requestAnimationFrame(function () {
        randomizeHeroBlocksWhenReady(attemptsLeft - 1);
      });
    }

    if (document.readyState === 'complete') {
      randomizeHeroBlocksWhenReady(120);
    } else {
      window.addEventListener('load', function () {
        randomizeHeroBlocksWhenReady(120);
      }, { once: true });
    }

    window.addEventListener('pageshow', function () {
      if (!_heroBlocksRandomized) {
        randomizeHeroBlocksWhenReady(120);
      }
    });

    // ── Bind drag events ──────────────────────────────────────
    blocks.forEach(function (block) {
      // Suppress native browser drag on media elements
      Array.prototype.forEach.call(block.querySelectorAll('img, video'), function (el) {
        el.setAttribute('draggable', 'false');
        el.addEventListener('dragstart', function (ev) { ev.preventDefault(); });
      });
      block.style.touchAction = 'none';
      block.addEventListener('dragstart', function (ev) { ev.preventDefault(); });
      block.addEventListener('pointerdown', onHeroBlockPointerDown);
    });

    var _dragging    = null;
    var _pointerId   = null;
    var _startMouseX = 0;
    var _startMouseY = 0;
    var _origLeft    = 0;
    var _origTop     = 0;

    function onHeroBlockPointerDown(e) {
      if (_dragging) return;
      e.preventDefault();
      _dragging = this;
      _pointerId = e.pointerId;
      var areaRect  = dragArea.getBoundingClientRect();
      var blockRect = _dragging.getBoundingClientRect();
      _origLeft    = blockRect.left - areaRect.left;
      _origTop     = blockRect.top  - areaRect.top;
      _startMouseX = e.clientX;
      _startMouseY = e.clientY;
      _dragging.style.left = _origLeft + 'px';
      _dragging.style.top  = _origTop  + 'px';
      _dragging.style.transform = 'translateZ(0)';
      _dragging.classList.add('hero-block--dragging');
      if (_dragging.setPointerCapture) _dragging.setPointerCapture(_pointerId);
      document.addEventListener('pointermove',   onHeroPointerMove);
      document.addEventListener('pointerup',     onHeroPointerUp);
      document.addEventListener('pointercancel', onHeroPointerUp);
    }

    function onHeroPointerMove(e) {
      if (!_dragging) return;
      if (e.pointerId !== _pointerId) return;
      var areaRect = dragArea.getBoundingClientRect();
      var newX = _origLeft + (e.clientX - _startMouseX);
      var newY = _origTop  + (e.clientY - _startMouseY);
      // X: keep within drag area width
      newX = Math.max(0, Math.min(newX, areaRect.width - _dragging.offsetWidth));
      // Y: allow dragging up into the header (above drag area top = negative offset),
      //    but not past the top or bottom of the viewport.
      var minY = -areaRect.top;
      var maxY = window.innerHeight - areaRect.top - _dragging.offsetHeight;
      newY = Math.max(minY, Math.min(newY, maxY));
      var dx = newX - _origLeft;
      var dy = newY - _origTop;
      _dragging.style.transform = 'translate(' + dx + 'px, ' + dy + 'px)';
    }

    function onHeroPointerUp(e) {
      if (!_dragging) return;
      if (e.pointerId !== _pointerId) return;
      // Commit final position to left/top and reset transform to plain GPU layer
      var areaRect  = dragArea.getBoundingClientRect();
      var blockRect = _dragging.getBoundingClientRect();
      _dragging.style.left      = (blockRect.left - areaRect.left) + 'px';
      _dragging.style.top       = (blockRect.top  - areaRect.top)  + 'px';
      _dragging.style.transform = 'translateZ(0)';
      _dragging.classList.remove('hero-block--dragging');
      if (_dragging.releasePointerCapture) _dragging.releasePointerCapture(_pointerId);
      _dragging = null;
      _pointerId = null;
      document.removeEventListener('pointermove',   onHeroPointerMove);
      document.removeEventListener('pointerup',     onHeroPointerUp);
      document.removeEventListener('pointercancel', onHeroPointerUp);
    }
  }

  // ── initOnce — persistent shell, runs one time ───────────────

  function initOnce() {
    // Active tab click → treat as home link (plays reverse/close animation)
    document.body.addEventListener('click', handleActiveTabClick);

    // Header height — must run BEFORE XericCart.init() so GSAP snaps the cart
    // to the correct closed-Y based on the actual rendered header height.
    updateHeaderH();

    // Cart init (persistent — survives all navigations)
    if (global.XericCart) {
      global.XericCart.init();
    }

    // Modal init (persistent — survives all navigations)
    if (global.XericModal) {
      global.XericModal.init();
    }

    // After fonts/images load the header may grow. Re-measure and re-snap.
    window.addEventListener('load', function () {
      updateHeaderH();
      updateMobTabTop();
      if (global.XericCart) global.XericCart.snap();
    }, { once: true });

    // ResizeObserver on .header-inner — fires whenever header grows/shrinks.
    if (window.ResizeObserver) {
      var _headerInnerObserver = new ResizeObserver(function () {
        updateHeaderH();
        updateMobTabTop();
      });
      var _headerInnerEl = document.querySelector('.header-inner');
      if (_headerInnerEl) _headerInnerObserver.observe(_headerInnerEl);
    }

    // Mobile tab anchor — measure header logo+links and keep --mob-tab-top live.
    updateMobTabTop();
    if (window.ResizeObserver) {
      var _headerObserver = new ResizeObserver(updateMobTabTop);
      var _linksEl = document.querySelector('.header__links');
      if (_linksEl) _headerObserver.observe(_linksEl);
    }

    // Hero drag — home-hero is persistent shell (outside Barba), init once only
    initHeroDrag();

    // ── Resize handler ──────────────────────────────────────────
    // GSAP stores pixel values derived from dvh at animation time.
    // When the window resizes, dvh changes so --panel-h changes, but the
    // GSAP inline transforms are stale. Re-snap everything on resize.
    let _resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(_resizeTimer);
      _resizeTimer = setTimeout(function () {
        // Re-measure header first so --header-h is current before any snap
        // calculations that derive panel-h and panel-closed-y from it.
        updateHeaderH();
        updateMobTabTop();

        const container = document.querySelector('[data-barba="container"]');
        const shell     = document.getElementById('site-shell');
        const tab       = shell ? shell.dataset.activeTab : 'none';
        const state     = (tab && tab !== 'none') ? 'open' : 'closed';

        if (global.XericAnimations) {
          global.XericAnimations.panelSnap(container, state);
        }
        if (global.XericCart) {
          global.XericCart.snap();
        }
      }, 100);
    });
  }

  /**
   * If the user clicks the tab they're already on:
   *   - On the TAB ROOT page (blog, collection): navigate home (close the panel).
   *   - On a CHILD page within the tab (article, product): navigate to the tab
   *     root instead, so the user can browse back without closing the panel.
   */
  function handleActiveTabClick(e) {
    const link = e.target.closest('.folder-tab[data-tab]');
    if (!link) return;

    const shell = document.getElementById('site-shell');
    if (!shell) return;

    const clickedTab  = link.dataset.tab;
    const currentTab  = shell.dataset.activeTab;

    // Only intercept when clicking the already-active tab
    if (clickedTab !== currentTab) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    // Child namespaces — clicking their tab navigates to the tab root, not home.
    const container = document.querySelector('[data-barba="container"]');
    const ns = container ? container.dataset.barbaNamespace : '';
    const TAB_CHILDREN = {
      'news': ['article'],
      'shop': ['product'],
    };
    const children = TAB_CHILDREN[clickedTab] || [];

    if (children.indexOf(ns) !== -1) {
      // Navigate to the tab root (use the tab's href directly)
      const dest = link.getAttribute('href');
      if (global.barba) {
        global.barba.go(dest);
      } else {
        window.location.href = dest;
      }
    } else {
      // On the root page — close the panel by navigating home
      if (global.barba) {
        global.barba.go('/');
      } else {
        window.location.href = '/';
      }
    }
  }

  // ── initAll — per-container init, idempotent ─────────────────

  function initAll(container, isInitial) {
    if (!container) return;

    // 1. Sync active-tab data attribute + panel position state
    syncTabState(container, isInitial);

    // 2. Refresh cart count on each navigation
    if (global.XericCart) {
      global.XericCart.refreshCount();
    }

    // 3. Shop search + filter — intercept form submit and select change
    //    so they route through Barba instead of causing full-page loads.
    bindShopFilters(container);

    // 4. Product form — AJAX add-to-cart (no redirect).
    bindProductForm(container);

    // 5. Product recommendations — Shopify Recommendations API.
    bindProductRecs(container);

    // 6. Product mobile gallery (stacked, instant swap).
    bindProductMobileGallery(container);

    // 7. Product image lightbox overlay.
    bindProductLightbox(container);
  }

  function bindProductMobileGallery(container) {
    var root = container.querySelector('.product-page');
    if (!root || root.dataset.mobileGalleryBound === 'true') return;

    var gallery = root.querySelector('[data-product-mobile-gallery]');
    var controls = root.querySelector('[data-product-mobile-gallery-controls]');
    var prevBtn = root.querySelector('[data-mobile-gallery-prev]');
    var nextBtn = root.querySelector('[data-mobile-gallery-next]');
    var triggers = Array.prototype.slice.call(root.querySelectorAll('[data-product-lightbox-image]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-mobile-gallery-dot]'));

    if (!gallery || !controls || !prevBtn || !nextBtn || triggers.length <= 1 || dots.length !== triggers.length) return;

    root.dataset.mobileGalleryBound = 'true';

    var mobileQuery = global.matchMedia('(max-width: 768px)');
    var currentIndex = 0;

    function applyState() {
      var isMobile = mobileQuery.matches;

      controls.hidden = !isMobile;

      triggers.forEach(function (trigger, index) {
        if (!isMobile) {
          trigger.hidden = false;
          trigger.removeAttribute('aria-hidden');
          return;
        }

        var isActive = index === currentIndex;
        trigger.hidden = !isActive;
        trigger.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      dots.forEach(function (dot, index) {
        var isActive = index === currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    function show(index) {
      if (index < 0) index = triggers.length - 1;
      if (index >= triggers.length) index = 0;
      currentIndex = index;
      applyState();
    }

    var onPrevClick = function () { show(currentIndex - 1); };
    var onNextClick = function () { show(currentIndex + 1); };

    prevBtn.addEventListener('click', onPrevClick);
    nextBtn.addEventListener('click', onNextClick);

    var dotHandlers = dots.map(function (dot, index) {
      var handler = function () { show(index); };
      dot.addEventListener('click', handler);
      return { dot: dot, handler: handler };
    });

    function onViewportChange() {
      applyState();
    }

    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', onViewportChange);
    } else if (mobileQuery.addListener) {
      mobileQuery.addListener(onViewportChange);
    }

    applyState();

    registerDestroy(function () {
      prevBtn.removeEventListener('click', onPrevClick);
      nextBtn.removeEventListener('click', onNextClick);
      dotHandlers.forEach(function (item) {
        item.dot.removeEventListener('click', item.handler);
      });

      if (mobileQuery.removeEventListener) {
        mobileQuery.removeEventListener('change', onViewportChange);
      } else if (mobileQuery.removeListener) {
        mobileQuery.removeListener(onViewportChange);
      }

      controls.hidden = false;
      triggers.forEach(function (trigger) {
        trigger.hidden = false;
        trigger.removeAttribute('aria-hidden');
      });
      dots.forEach(function (dot) {
        dot.classList.remove('is-active');
        dot.removeAttribute('aria-current');
      });

      root.dataset.mobileGalleryBound = 'false';
    });
  }

  function bindProductLightbox(container) {
    var root = container.querySelector('.product-page');
    if (!root || root.dataset.lightboxBound === 'true') return;

    var lightbox = root.querySelector('[data-product-lightbox]');
    var target = root.querySelector('[data-lightbox-target]');
    var closeBtn = root.querySelector('[data-lightbox-close]');
    var prevBtn = root.querySelector('[data-lightbox-prev]');
    var nextBtn = root.querySelector('[data-lightbox-next]');
    var triggers = Array.prototype.slice.call(root.querySelectorAll('[data-product-lightbox-image]'));

    if (!lightbox || !target || !closeBtn || !prevBtn || !nextBtn || !triggers.length) return;

    var originalParent = lightbox.parentNode;
    var originalNextSibling = lightbox.nextSibling;

    function syncLightboxVars() {
      var source = global.getComputedStyle(root);
      [
        '--lightbox-overlay-rgb',
        '--lightbox-overlay-alpha',
        '--lightbox-max-width',
        '--lightbox-max-height',
        '--lightbox-arrow-size',
        '--lightbox-close-size',
      ].forEach(function (name) {
        var value = source.getPropertyValue(name);
        if (value && value.trim()) {
          lightbox.style.setProperty(name, value.trim());
        }
      });
    }

    syncLightboxVars();

    if (lightbox.parentNode !== document.body) {
      document.body.appendChild(lightbox);
    }

    root.dataset.lightboxBound = 'true';

    var currentIndex = 0;
    var mobileQuery = global.matchMedia('(max-width: 768px)');

    function isMobileLightboxDisabled() {
      return mobileQuery.matches;
    }

    function render(index) {
      if (index < 0) index = triggers.length - 1;
      if (index >= triggers.length) index = 0;
      currentIndex = index;

      var sourceEl = triggers[currentIndex];
      var src = sourceEl.getAttribute('data-lightbox-src');
      var thumb = sourceEl.querySelector('img');

      target.src = src || '';
      target.alt = thumb ? thumb.alt : '';
    }

    function open(index) {
      if (isMobileLightboxDisabled()) return;
      syncLightboxVars();
      render(index);
      lightbox.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    function close() {
      lightbox.hidden = true;
      document.body.style.overflow = '';
    }

    var triggerHandlers = triggers.map(function (trigger, index) {
      var handler = function (event) {
        if (isMobileLightboxDisabled()) return;
        event.preventDefault();
        open(index);
      };
      trigger.addEventListener('click', handler);
      return { trigger: trigger, handler: handler };
    });

    var onCloseClick = function () { close(); };
    var onPrevClick = function () { render(currentIndex - 1); };
    var onNextClick = function () { render(currentIndex + 1); };
    var onBackdropClick = function (event) {
      if (event.target === lightbox) close();
    };
    var onKeydown = function (event) {
      if (lightbox.hidden) return;
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') render(currentIndex - 1);
      if (event.key === 'ArrowRight') render(currentIndex + 1);
    };

    closeBtn.addEventListener('click', onCloseClick);
    prevBtn.addEventListener('click', onPrevClick);
    nextBtn.addEventListener('click', onNextClick);
    lightbox.addEventListener('click', onBackdropClick);
    document.addEventListener('keydown', onKeydown);

    registerDestroy(function () {
      close();
      triggerHandlers.forEach(function (item) {
        item.trigger.removeEventListener('click', item.handler);
      });
      closeBtn.removeEventListener('click', onCloseClick);
      prevBtn.removeEventListener('click', onPrevClick);
      nextBtn.removeEventListener('click', onNextClick);
      lightbox.removeEventListener('click', onBackdropClick);
      document.removeEventListener('keydown', onKeydown);
      if (originalParent && originalParent.isConnected) {
        originalParent.insertBefore(lightbox, originalNextSibling);
      } else if (lightbox.parentNode === document.body) {
        lightbox.remove();
      }
      root.dataset.lightboxBound = 'false';
    });
  }

  /**
   * Intercepts the [data-add-to-cart] button on the product page.
   * Posts to /cart/add.js via fetch so the page never navigates,
   * then refreshes the cart count and opens the cart drawer.
   *
   * @param {HTMLElement} container
   */
  function bindProductForm(container) {
    var atcBtn = container.querySelector('[data-add-to-cart]');
    var buyBtn = container.querySelector('[data-buy-now]');
    if (!atcBtn && !buyBtn) return;

    // Walk up to the wrapping <form> (use whichever button exists)
    var form = (atcBtn || buyBtn).closest('form');
    if (!form) return;

    var qtyNotice = form.querySelector('[data-product-qty-notice]');

    function hideQtyNotice() {
      if (!qtyNotice) return;
      qtyNotice.hidden = true;
      qtyNotice.textContent = '';
    }

    function showQtyNotice(message) {
      if (!qtyNotice) return;
      var fallback = qtyNotice.dataset.defaultMessage || 'Unable to add this quantity right now.';
      qtyNotice.textContent = message || fallback;
      qtyNotice.hidden = false;
    }

    function getCartErrorMessage(errObj) {
      if (!errObj) return null;
      if (typeof errObj === 'string') return errObj;
      return errObj.description || errObj.message || null;
    }

    /**
     * Shared helper: POST to /cart/add.js and return the response JSON.
     * Rejects if the request fails.
     */
    var addToCart = async function (variantId, quantity) {
      var res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ id: variantId, quantity: quantity }),
      });
      if (!res.ok) {
        var err = await res.json().catch(function () { return {}; });
        throw err;
      }
      return res.json();
    };

    var getFormValues = function () {
      var variantInput = form.querySelector('select[name="id"]') ||
                         form.querySelector('input[name="id"]');
      var qtyInput     = form.querySelector('input[name="quantity"]');
      return {
        variantId: variantInput ? variantInput.value : null,
        quantity:  qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1,
      };
    };

    // ── Add to Cart: AJAX add → open drawer ────────────────
    if (atcBtn) {
      var onAtcClick = async function (e) {
        e.preventDefault();
        var _ref = getFormValues(), variantId = _ref.variantId, quantity = _ref.quantity;
        if (!variantId) return;
        atcBtn.disabled = true;
        hideQtyNotice();
        try {
          await addToCart(variantId, quantity);
          if (global.XericCart) {
            await global.XericCart.refreshCount();
            global.XericCart.open();
          }
        } catch (err) {
          console.warn('[XericApp] add to cart failed', err);
          showQtyNotice(getCartErrorMessage(err));
          if (global.XericCart) global.XericCart.refreshCount();
        } finally {
          atcBtn.disabled = false;
        }
      };
      atcBtn.addEventListener('click', onAtcClick);
      registerDestroy(function () {
        atcBtn.removeEventListener('click', onAtcClick);
      });
    }

    // ── Buy Now: AJAX add → redirect to /checkout ──────────
    if (buyBtn) {
      var onBuyClick = async function (e) {
        e.preventDefault();
        var _ref2 = getFormValues(), variantId = _ref2.variantId, quantity = _ref2.quantity;
        if (!variantId) return;
        buyBtn.disabled = true;
        hideQtyNotice();
        try {
          await addToCart(variantId, quantity);
          window.location.href = '/checkout';
        } catch (err) {
          console.warn('[XericApp] buy now failed', err);
          showQtyNotice(getCartErrorMessage(err));
          if (global.XericCart) global.XericCart.refreshCount();
          buyBtn.disabled = false;
        }
      };
      buyBtn.addEventListener('click', onBuyClick);
      registerDestroy(function () {
        buyBtn.removeEventListener('click', onBuyClick);
      });
    }
  }

  /**
   * Injects Shopify algorithm-based recommended products into the
   * [data-product-recs] grid on the product page.
   *
   * @param {HTMLElement} container
   */
  function bindProductRecs(container) {
    var grid = container.querySelector('[data-product-recs]');
    if (!grid) return;

    var productId = grid.dataset.productRecs;
    if (!productId) return;

    var url = '/recommendations/products.json?product_id=' + productId + '&limit=4';

    // Resize a Shopify CDN URL: injects `_400x` before the file extension.
    function cdnSize(src, size) {
      return src.replace(/(\.[a-zA-Z]+)(\?|$)/, '_' + size + '$1$2');
    }

    fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = data.products || [];
        if (!products.length) return;

        var html = products.map(function (p) {
          // Recommendations API returns featured_image as a plain URL string,
          // product objects from other endpoints use { src, alt }.
          var imgSrc = null;
          var imgAlt = '';
          if (typeof p.featured_image === 'string' && p.featured_image) {
            imgSrc = p.featured_image;
          } else if (p.featured_image && p.featured_image.src) {
            imgSrc = p.featured_image.src;
            imgAlt = p.featured_image.alt || '';
          } else if (p.images && p.images[0]) {
            imgSrc = typeof p.images[0] === 'string' ? p.images[0] : p.images[0].src;
            imgAlt = (p.images[0] && p.images[0].alt) || '';
          }
          var imgHtml = imgSrc
            ? '<img class="shop-card__image" src="' + cdnSize(imgSrc, '400x') +
              '" alt="' + imgAlt.replace(/"/g, '&quot;') + '">'
            : '';
          var titleHtml = '<span class="shop-card__title">' + p.title.replace(/</g, '&lt;') + '</span>';
          return '<a href="' + p.url + '" class="shop-card">' + imgHtml + titleHtml + '</a>';
        }).join('');

        grid.innerHTML = html;
      })
      .catch(function (err) {
        console.warn('[XericApp] recommendations failed', err);
      });
  }

  /**
   * Client-side field-scoped search for the shop panel.
   *
   * "Filter by" select chooses WHICH field to search.
   * Search input queries WITHIN that field.
   * Switching the field clears the input and resets cards.
   * SHOP ALL navigates normally via Barba — intentional.
   *
   * @param {HTMLElement} container
   */
  function bindShopFilters(container) {
    var searchInput  = container.querySelector('.shop-filter-bar__search');
    var searchForm   = container.querySelector('.shop-filter-bar__search-form');
    var typeSelect   = container.querySelector('.shop-filter-bar__filter--type');
    var familySelect = container.querySelector('.shop-filter-bar__filter--family');
    var cards        = Array.prototype.slice.call(container.querySelectorAll('.shop-card'));

    if (!cards.length) return;

    var currentSearch = '';
    var currentType   = '';
    var currentFamily = '';

    // Populate a select with unique non-empty values from a card dataset key.
    // Preserves the first "All" option already in the HTML.
    function populateSelect(select, values) {
      if (!select) return;
      while (select.options.length > 1) select.remove(1);
      values.forEach(function (v) {
        var opt = document.createElement('option');
        opt.value = v;
        opt.textContent = v.charAt(0).toUpperCase() + v.slice(1);
        select.appendChild(opt);
      });
    }

    function uniqueSorted(arr) {
      var seen = {};
      var out = [];
      arr.forEach(function (v) {
        v = (v || '').trim();
        if (v && !seen[v]) { seen[v] = true; out.push(v); }
      });
      return out.sort();
    }

    // Populate Type with all types on screen
    populateSelect(typeSelect, uniqueSorted(cards.map(function (c) {
      return c.dataset.fieldPlantType || '';
    })));

    // Populate Family based on cards matching the current type filter
    function refreshFamilyOptions() {
      var matchingCards = currentType
        ? cards.filter(function (c) { return (c.dataset.fieldPlantType || '') === currentType; })
        : cards;
      populateSelect(familySelect, uniqueSorted(matchingCards.map(function (c) {
        return c.dataset.fieldPlantFamily || '';
      })));
      if (familySelect) familySelect.value = '';
      currentFamily = '';
    }

    refreshFamilyOptions();

    function applyFilters() {
      cards.forEach(function (card) {
        var type   = (card.dataset.fieldPlantType   || '');
        var family = (card.dataset.fieldPlantFamily || '');
        var title  = (card.dataset.fieldTitle       || '');
        var typeMatch   = !currentType   || type   === currentType;
        var familyMatch = !currentFamily || family === currentFamily;
        var searchMatch = !currentSearch || title.indexOf(currentSearch) !== -1;
        card.style.display = (typeMatch && familyMatch && searchMatch) ? '' : 'none';
      });
    }

    // Block native form submission — all filtering is in-DOM
    if (searchForm) {
      var onSearchSubmit = function (e) { e.preventDefault(); };
      searchForm.addEventListener('submit', onSearchSubmit);
      registerDestroy(function () {
        searchForm.removeEventListener('submit', onSearchSubmit);
      });
    }

    // Live title search
    if (searchInput) {
      var onSearchInput = function () {
        currentSearch = (searchInput.value || '').trim().toLowerCase();
        applyFilters();
      };
      searchInput.addEventListener('input', onSearchInput);
      registerDestroy(function () {
        searchInput.removeEventListener('input', onSearchInput);
      });
    }

    // Plant Type dropdown
    if (typeSelect) {
      var onTypeChange = function () {
        currentType = typeSelect.value || '';
        // Repopulate family to only show families present in matching cards
        refreshFamilyOptions();
        applyFilters();
      };
      typeSelect.addEventListener('change', onTypeChange);
      registerDestroy(function () {
        typeSelect.removeEventListener('change', onTypeChange);
      });
    }

    // Plant Family dropdown
    if (familySelect) {
      var onFamilyChange = function () {
        currentFamily = familySelect.value || '';
        applyFilters();
      };
      familySelect.addEventListener('change', onFamilyChange);
      registerDestroy(function () {
        familySelect.removeEventListener('change', onFamilyChange);
      });
    }
  }

  /**
   * Sync the shell's data-active-tab based on the current Barba namespace.
   * Also ensures panel translateY is correct for the current page.
   *
   * @param {HTMLElement} container
   * @param {boolean} [isInitial=false]  true on first DOMContentLoaded load;
   *   defers the snap to a double-rAF so dvh layout has flushed.
   */
  function syncTabState(container, isInitial) {
    const ns     = container.dataset.barbaNamespace || '';
    const tab    = (global.XericBarba && global.XericBarba.getTabForNamespace(ns)) || 'none';
    const shell  = document.getElementById('site-shell');

    if (shell) {
      shell.dataset.activeTab = tab;
      shell.dataset.activeNamespace = ns;
    }

    // /cart has its own full-page cart UI; ensure drawer state cannot overlay it.
    if (tab === 'cart' && global.XericCart) {
      global.XericCart.close();
      global.XericCart.snap();
    }

    if (!global.XericAnimations) return;

    const state = tab === 'none' ? 'closed' : 'open';

    if (isInitial) {
      /*
       * On initial page load, defer until after two animation frames so
       * dvh-derived heights are fully resolved before GSAP reads them.
       * Use panelOpen (animation) for tab pages so a direct URL load to
       * /collections still shows the satisfying slide-up reveal.
       * Use panelSnap (instant) for home so the docked state is immediate.
       */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (state === 'open') {
            global.XericAnimations.panelOpen(container);
          } else {
            global.XericAnimations.panelSnap(container, 'closed');
          }
        });
      });
    } else {
      // Post-Barba transition: layout is already flushed, snap immediately.
      global.XericAnimations.panelSnap(container, state);
    }
  }

  // ── destroyAll — teardown per-container listeners ────────────

  function destroyAll() {
    _destroyFns.forEach(fn => {
      try { fn(); } catch (e) { /* Don't let one failure block others */ }
    });
    _destroyFns = [];
  }

  // ── DOMContentLoaded bootstrap ───────────────────────────────

  document.addEventListener('DOMContentLoaded', function () {

    // Expose XericApp globally so barba.js hooks can call it
    global.XericApp = {
      initAll,
      destroyAll,
      registerDestroy,
    };

    // 1. Run initOnce for persistent shell
    initOnce();

    // 2. Run initAll for the initial page container
    const initialContainer = document.querySelector('[data-barba="container"]');
    if (initialContainer) {
      initAll(initialContainer, /* isInitial= */ true);
    }

    // 3. Bootstrap Barba (after initOnce + initAll so auth state is ready)
    if (global.XericBarba) {
      global.XericBarba.init();
    }

  });

})(window);
